
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import DialogBanner from "@/components/app/DialogBanner.vue";
import Booking from "@/domain/booking/Booking";
import Table from "@/domain/restaurant/Table";
import RemoteServices from "@/services/RemoteServices";
import TablePicker from "@/components/booking/components/TablePicker.vue";
import BookingOverviewComponent from "@/components/booking/components/BookingOverviewComponent.vue";
import Restaurant from "@/domain/restaurant/Restaurant";
import BookingList from "@/components/booking/BookingList.vue";
import Room from "@/domain/restaurant/Room";
@Component({
  components: {
    BookingList,
    BookingOverviewComponent,
    TablePicker,
    DialogBanner
  }
})
export default class BookingChangeTableDialog extends Vue {
  @Prop({ default: false })
  value!: boolean;

  @Prop({ default: new Restaurant() })
  selectedRestaurant!: Restaurant;

  @Prop({ default: new Booking() })
  booking!: Booking;

  @Prop({ default: [] })
  bookingList!: Booking[];

  @Prop({ default: {} })
  tables!: { [key: number]: Table };

  @Prop({ default: {} })
  rooms!: { [key: number]: Room };

  headers = [
    {
      text: this.$t("booking.time"),
      value: "bookingDateTime",
      groupable: false,
      width: 100
    },
    {
      text: this.$t("booking.numberOfPeople"),
      value: "numberOfPeople",
      groupable: false
    },
    {
      text: this.$t("booking.customerName"),
      value: "customerName",
      groupable: false
    },

    {
      text: this.$t("booking.table.label"),
      value: "tableId",
      groupable: false
    },

    {
      text: this.$t("booking.status.label"),
      value: "status",
      groupable: false
    }
  ];

  loadingTables = false;

  loading = false;

  selectedTableId: number | null = null;

  potentialTables: Table[] = [];
  get dialog() {
    return this.value;
  }

  set dialog(newValue) {
    this.$emit("input", newValue);
  }

  closeAndResetDialog() {
    this.dialog = false;
    this.selectedTableId = null;
  }

  get customerName() {
    if (this.booking != null && this.booking.customerName !== undefined) {
      return this.booking.getTruncatedCustomerName();
    }
    return "";
  }

  hasBookingBefore(booking: Booking) {
    if (booking.tableId != null) {
      const affectedTableIds = Table.getAllAffectedTableIds(
        this.tables[booking.tableId]
      );
      const bookings: Booking[] = this.bookingList.filter(b =>
        affectedTableIds.includes(b.tableId as number)
      );
      const timeString = booking.getBookingTime();
      if (bookings != null && bookings.length > 1) {
        for (let i = 0; i < bookings.length; i++) {
          if (timeString > bookings[i].getBookingTime()) return true;
        }
      }
    }
    return false;
  }

  get bookingsOnSameTableAsSelected() {
    // console.log("computing bookings on same table");
    const selectedTablesId: Set<number> = new Set();
    if (this.booking.tableId != null) {
      const tableIds: number[] = [];
      tableIds.push(this.booking.tableId as number);
      tableIds.push(this.selectedTableId as number);
      let table = null;
      for (let j = 0; j < tableIds.length; j++) {
        for (let k = 0; k < this.potentialTables.length; k++) {
          if (this.potentialTables[k].tableId === tableIds[j])
            table = this.potentialTables[k];
        }
        if (table == null) {
          console.log("bookingsOnSameTableAsSelected, table not found");
          break;
        }
        const affectedIds = Table.getAllAffectedTableIds(table); // TODO maybe compute on creation for all tables
        for (let i = 0; i < affectedIds.length; i++)
          selectedTablesId.add(affectedIds[i]);
      }
    }
    return this.bookingList.filter(b => {
      if (b.tableId !== undefined)
        return (
          selectedTablesId.has(b.tableId) &&
          b.bookingId !== this.booking.bookingId
        );
      return false;
    });
  }

  @Watch("dialog")
  async fetchPotentialSwaps() {
    if (this.dialog) {
      this.loadingTables = true;
      this.potentialTables = [];
      try {
        this.potentialTables = await RemoteServices.getPotentialTablesToSwapBooking(
          this.booking
        );
        this.potentialTables.sort(Table.TablesByNameAscendingComparator);
      } catch (error) {
        await this.$store.dispatch("error", error.message);
      }
      this.loadingTables = false;
    }
  }

  getRoomColour(tableId: number) {
    const table = this.tables[tableId];
    let room;
    if (table != null) room = this.rooms[table.roomId];
    if (room != null) return Room.getRoomColourFromId(room.roomId);
    return "red";
  }

  getBookingTableAssignmentIcon(booking: Booking) {
    if (this.selectedRestaurant != null)
      return booking.getTableAssignmentIcon(this.selectedRestaurant, false)
        .icon;
    return "";
  }

  getBookingTableAssignmentIconColour(booking: Booking) {
    if (this.selectedRestaurant != null)
      return booking.getTableAssignmentIcon(this.selectedRestaurant, false)
        .colour;
    return "";
  }

  getBookingStatusIcon(booking: Booking) {
    if (this.selectedRestaurant != null) {
      return booking.getBookingStatusIcon().icon;
    }
    return "";
  }

  getBookingStatusIconColour(booking: Booking) {
    if (this.selectedRestaurant != null)
      return booking.getBookingStatusIcon().colour;
    return "";
  }

  getTableName(tableId: number | null): string | null {
    if (tableId != null && this.tables[tableId] != null)
      return this.tables[tableId].tableName;
    return this.$tc("booking.table.not.selected");
  }

  selectTable(tableId: number) {
    this.selectedTableId = tableId;
  }

  get canSubmit() {
    return (
      this.selectedTableId != null &&
      this.booking.bookingId != null &&
      this.potentialTables.length > 0
    );
  }

  async quickChangeBookingTable() {
    if (this.canSubmit) {
      this.loading = true;
      try {
        let bookingCopy = Booking.copy(this.booking);
        bookingCopy.tableId = this.selectedTableId as number;
        bookingCopy = await RemoteServices.quickChangeBookingTable(bookingCopy);
        await this.$store.dispatch(
          "success",
          this.$t("booking.changeTable.success")
        );
        this.emitQuickChange(bookingCopy);
        this.closeAndResetDialog();
      } catch (error) {
        await this.$store.dispatch("error", error.message);
      }
      this.loading = false;
    }
  }

  emitQuickChange(booking: Booking) {
    this.$emit("booking-quick-change-table", this.booking);
  }
}
